import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  TextField,
  FormHelperText
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../../../components/CustomButton/Button";
import axios from "axios";

interface BusinessOption {
  id: string;
  name: string;
}

interface AddMembershipDialogProps {
  open: boolean;
  onClose: () => void;
  // Remove onConfirm, loading, error from parent, handle all here
  userId: string;
  businesses: BusinessOption[];
  onSuccess?: () => void; // Optional callback after successful add
}

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

const AddMembershipDialog: React.FC<AddMembershipDialogProps> = ({
  open,
  onClose,
  userId,
  businesses,
  onSuccess
}) => {
  const [businessId, setBusinessId] = React.useState("");
  const [validationError, setValidationError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const validateBusinessId = (id: string) => {
    // UUID format validation (basic check)
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(id)) {
      return "Please enter a valid Business ID (UUID format)";
    }
    return "";
  };

  const handleConfirm = async () => {
    const trimmedId = businessId.trim();
    if (!trimmedId) {
      setValidationError("Business ID is required");
      return;
    }
    const validationError = validateBusinessId(trimmedId);
    if (validationError) {
      setValidationError(validationError);
      return;
    }
    setValidationError("");
    setError("");
    setLoading(true);

    try {
      await axios.post(
        `${apiEndpoint}/loyaltyprogram/membership`,
        {
          UserId: userId,
          BusinessId: trimmedId
        },
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      setBusinessId("");
      setLoading(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        "Failed to add membership. Please try again.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setBusinessId("");
    setValidationError("");
    setError("");
    setLoading(false);
    onClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBusinessId(e.target.value);
    if (validationError) {
      setValidationError("");
    }
    if (error) {
      setError("");
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontWeight: 600, fontSize: "18px" }}>
            Add Membership
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {(error || validationError) && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
            {error || validationError}
          </Alert>
        )}
        <Typography sx={{ mb: 2 }}>
          Enter the Business ID (UUID) of the business you want to add a
          membership for.
        </Typography>
        <TextField
          fullWidth
          label="Business ID"
          value={businessId}
          onChange={handleInputChange}
          disabled={loading}
          placeholder="Enter Business ID (e.g., b02d3127-1490-4341-ac71-5c8ea27b0dd0)"
          error={!!validationError}
          sx={{ mt: 1 }}
        />
        <FormHelperText sx={{ mt: 1, color: "text.secondary" }}>
          Format: UUID (e.g., xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
        </FormHelperText>
      </DialogContent>
      <DialogActions>
        <CustomButton callBack={handleClose} disabled={loading}>
          Cancel
        </CustomButton>
        <CustomButton
          callBack={handleConfirm}
          disabled={loading || !businessId.trim()}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Add"}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddMembershipDialog;
